import type { UseFormReturn } from 'react-hook-form';
import { MDXEditorMethods } from '@mdxeditor/editor';
import {
  Labeled,
  OptionCard,
  Radio,
  RadioGroup,
  Switch,
  TextInputField,
} from '@sixui/ui';
import { spacingTokens } from '@sixui/ui/themes/base/spacing.stylex';
import stylex from '@stylexjs/stylex';
import { useTranslation } from 'react-i18next';

import type { IContainerProps } from '~/components/utils/Container';
import type { ILegalDocumentFormValues } from './LegalDocumentForm';
import { FormField } from '~/components/atoms/FormField';
import { FormSection } from '~/components/atoms/FormSection';
import { MarkdownEditor } from '~/components/organisms/fields/MarkdownEditor';
import { IFormMode } from '~/helpers/types';

export type ILegalDocumentFormMainSectionProps = IContainerProps & {
  form: UseFormReturn<ILegalDocumentFormValues>;
  mode: IFormMode;
  defaultMarkdown?: string;
  markdownEditorRef?: React.RefObject<MDXEditorMethods>;
};

const styles = stylex.create({
  typeOptions: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gridAutoRows: '1fr',
    gap: spacingTokens.padding$2,
  },
});

export const LegalDocumentFormMainSection: React.FC<
  ILegalDocumentFormMainSectionProps
> = (props) => {
  const { sx, form, defaultMarkdown, markdownEditorRef } = props;
  const { t } = useTranslation();

  return (
    <FormSection sx={sx}>
      <Labeled
        label={t('LegalDocumentFormMainSection.field.type.label')}
        required
      >
        <FormField
          name="type"
          control={form.control}
          as={RadioGroup}
          rules={{ required: true }}
          sx={styles.typeOptions}
        >
          <OptionCard
            as={Radio}
            label={t(
              'LegalDocumentFormMainSection.field.type.options.tos.label',
            )}
            value="tos"
          >
            {t(
              'LegalDocumentFormMainSection.field.type.options.tos.supportingText',
            )}
          </OptionCard>
          <OptionCard
            as={Radio}
            label={t(
              'LegalDocumentFormMainSection.field.type.options.privacy.label',
            )}
            value="privacy"
          >
            {t(
              'LegalDocumentFormMainSection.field.type.options.privacy.supportingText',
            )}
          </OptionCard>
        </FormField>
      </Labeled>
      <Labeled label={t('common.title')} required>
        <FormField
          name="title"
          control={form.control}
          as={TextInputField}
          forwardError
        />
      </Labeled>
      <Labeled label={t('common.text')} required>
        <FormField
          name="markdown"
          control={form.control}
          as={MarkdownEditor}
          markdown={defaultMarkdown}
          ref={markdownEditorRef}
          forwardError
        />
      </Labeled>
      <Labeled
        label={t('LegalDocumentFormMainSection.field.required.label')}
        supportingText={t(
          'LegalDocumentFormMainSection.field.required.supportingText',
        )}
        labelPosition="right"
      >
        <FormField name="required" control={form.control} as={Switch} />
      </Labeled>
      <Labeled
        label={t('LegalDocumentFormMainSection.field.outdated.label')}
        labelPosition="right"
      >
        <FormField name="outdated" control={form.control} as={Switch} />
      </Labeled>
    </FormSection>
  );
};
